import React, { useState, useEffect, useMemo } from 'react';
import { Star, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import testimonialService, { PublicTestimonial } from '../../services/user/testimonialService';
import { getImageUrl } from '../../utils/imageHelper';

const Testimonials: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation();
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const data = await testimonialService.getAllTestimonials();

      // Sort: featured first, then by date (newest first)
      const sorted = [...data.testimonials].sort((a, b) => {
        const aFeatured = Boolean(a.featured);
        const bFeatured = Boolean(b.featured);
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setTestimonials(sorted);
    } catch (error: any) {
      console.error('Failed to fetch testimonials:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to render stars for rating
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={20}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
        <span className="ml-2 text-lg font-semibold text-secondary-900">({rating}/5)</span>
      </div>
    );
  };

  // Get unique categories
  const categories: string[] = ['All', ...Array.from(new Set(testimonials.map(t => t.category).filter((c): c is string => Boolean(c))))];

  // Filter testimonials by category
  const filteredTestimonials = useMemo(() => {
    const filtered = selectedCategory === 'All'
      ? testimonials
      : testimonials.filter(t => t.category === selectedCategory);
    return filtered;
  }, [testimonials, selectedCategory]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTestimonials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTestimonials = filteredTestimonials.slice(startIndex, endIndex);

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 md:pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-[#0c513f]/5 rounded-full blur-[100px] animate-blob"></div>
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-chowdeck-pastelYellow/20 rounded-full blur-[120px] animate-blob animation-delay-2000"></div>
        </div>

        <div className={`container-premium text-center transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
          }`}>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            Customer Stories
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            See what our customers have to say about their experience with Rita.
            Real stories from real people.
          </p>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 1 && (
        <section className="py-8 px-4 border-b border-[#0c513f]/5 bg-white/50 backdrop-blur-md sticky top-20 z-30">
          <div className="container-premium">
            <div className="flex flex-wrap gap-4 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-8 py-3 rounded-full font-bold text-lg transition-all duration-300 ${selectedCategory === category
                    ? 'bg-[#0c513f] text-[#faf8ed] shadow-lg scale-105'
                    : 'bg-white border-2 border-secondary-100 text-secondary-600 hover:border-[#0c513f] hover:text-[#0c513f] hover:scale-105'
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Grid */}
      <section ref={testimonialsRef} className="py-20 px-4">
        <div className="container-premium">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-[#0c513f]" size={48} />
            </div>
          ) : filteredTestimonials.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {paginatedTestimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className={`bg-white rounded-[2.5rem] p-6 md:p-10 border-2 border-transparent hover:border-[#0c513f]/10 shadow-sm hover:shadow-2xl transition-all duration-500 group hover:scale-[1.02] hover:-translate-y-2 ${testimonialsVisible
                      ? `animate-fade-in-up opacity-100`
                      : 'opacity-0'
                      } ${Boolean(testimonial.featured) ? 'ring-4 ring-[#0c513f]/10 bg-[#faf8ed]' : ''}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex items-center space-x-4">
                        {testimonial.profile ? (
                          <img
                            src={getImageUrl(testimonial.profile) || testimonial.profile}
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-md"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.innerHTML = `<div class="w-16 h-16 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-bold text-2xl border-4 border-white shadow-md">${testimonial.name.charAt(0).toUpperCase()}</div>`;
                              }
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-bold text-2xl border-4 border-white shadow-md">
                            {testimonial.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h4 className="font-black text-[#0c513f] text-xl">{testimonial.name}</h4>
                          {testimonial.location && (
                            <p className="text-sm text-secondary-500 font-bold">{testimonial.location}</p>
                          )}
                        </div>
                      </div>
                      {Boolean(testimonial.verified) && (
                        <div className="bg-[#0c513f]/10 text-[#0c513f] p-2 rounded-full">
                          <Star size={16} fill="currentColor" />
                        </div>
                      )}
                    </div>

                    {Boolean(testimonial.featured) && (
                      <div className="mb-6">
                        <span className="bg-[#0c513f] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                          Featured Story
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      {renderStars(testimonial.rating)}
                    </div>

                    <p className="text-secondary-700 leading-relaxed mb-8 text-lg font-medium italic">
                      "{testimonial.text}"
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-secondary-100">
                      {testimonial.category && (
                        <span className="text-xs font-bold text-[#0c513f] bg-[#0c513f]/5 px-3 py-1 rounded-full uppercase tracking-wider">
                          {testimonial.category}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-16 flex items-center justify-center gap-4 flex-wrap">
                  {/* Previous Button */}
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="w-14 h-14 rounded-full border-2 border-secondary-200 flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0c513f] hover:text-white hover:border-[#0c513f]"
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={24} />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-3 bg-white p-2 rounded-full shadow-sm">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      if (!showPage) {
                        // Show ellipsis
                        const prevPage = page - 1;
                        const nextPage = page + 1;
                        if (
                          (prevPage === 1 || nextPage === totalPages) &&
                          !Array.from({ length: totalPages }, (_, i) => i + 1)
                            .slice(Math.max(0, currentPage - 2), Math.min(totalPages, currentPage + 2))
                            .includes(page)
                        ) {
                          return (
                            <span key={page} className="w-10 flex justify-center text-secondary-400">
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageClick(page)}
                          className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === page
                            ? 'bg-[#0c513f] text-white shadow-lg scale-110'
                            : 'text-secondary-500 hover:bg-secondary-100'
                            }`}
                          aria-label={`Go to page ${page}`}
                          aria-current={currentPage === page ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="w-14 h-14 rounded-full border-2 border-secondary-200 flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0c513f] hover:text-white hover:border-[#0c513f]"
                    aria-label="Next page"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
              )}

              {/* Pagination Info */}
              {totalPages > 1 && (
                <div className="mt-6 text-center text-secondary-500 font-medium">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredTestimonials.length)} of {filteredTestimonials.length} stories
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-2xl text-secondary-600 font-bold">
                No stories found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Testimonials;
